/**
 *      ▄ █
 *    █ █ ▀▀█  LOADBALANCER CLIENT
 *    █ ▀ ▀ █  @copyright Copyright (c) 2025
 */
import { BaseComponent } from '../../../../../core/components';
import { Component } from '@angular/core';
import {
    EnduranceRBACGroupsApiService,
    EnduranceRBACRolesApiService,
    EnduranceRBACUsersApiService
} from '../../../shared/http';
import { ErrorResponseModel } from '../../../../../core/models';
import { LogService, NavigationService } from '../../../../../core/services';
import { Router } from '@angular/router';
import { forkJoin } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

@Component({
    selector: 'app-user-management',
    templateUrl: './endurance-user-list.component.html'
})

export class EnduranceUserListComponent extends BaseComponent {
    /**
     * Page data.
     */
    public data: { users: any, roles: any, groups: any, groupPatch?: any };

    /**
     * User button permissions.
     */
    public buttonReference: any = {
        'rbac-add-user': [{ path: '/rbac/users', methods: ['POST'] }],
        'rbac-user-edit': [{ path: '/rbac/users/:id', methods: ['PUT'] }],
        'rbac-user-delete': [{ path: '/rbac/users/:id', methods: ['DELETE'] }],

        'rbac-add-role': [{ path: '/rbac/roles', methods: ['POST'] }],
        'rbac-role-edit': [{ path: '/rbac/roles/:id', methods: ['PUT'] }],
        'rbac-role-duplicate': [{ path: '/rbac/roles/:id', methods: ['PUT'] }],
        'rbac-role-delete': [{ path: '/rbac/roles/:id', methods: ['DELETE'] }],

        'rbac-add-group': [{ path: '/rbac/groups', methods: ['POST'] }],
        'rbac-group-edit': [{ path: '/rbac/groups/:id', methods: ['PUT'] }],
        'rbac-group-delete': [{ path: '/rbac/groups/:id', methods: ['DELETE'] }],
    };

    /**
     * Store appliance ID for routing.
     * @private
     */
    private readonly applianceId: string;

    constructor(
        private enduranceRBACGroupsApiService: EnduranceRBACGroupsApiService,
        private enduranceRBACRolesApiService: EnduranceRBACRolesApiService,
        private enduranceRBACUsersApiService: EnduranceRBACUsersApiService,
        private logService: LogService,
        private navigationService: NavigationService,
        private router: Router
    ) {
        super();

        this.applianceId = this.navigationService.getApplianceId();

        this.setUpPageData();
    }

    /**
     * Redirect to add or edit user page
     * @param id
     */
    public addEditUser(id?: string): void {
        const urlMain: string = `/lb/${this.applianceId}/maintenance/users/user`;
        const url: string = id ? `${urlMain}/edit/${id}` : urlMain + '/add';
        this.router.navigateByUrl(url).then().catch();
    }

    /**
     * Redirect to add or edit role page
     * @param id
     * @param duplicate
     */
    public addEditRole({ id, duplicate = false }): void {
        const urlMain: string = `/lb/${this.applianceId}/maintenance/users/role`;
        const url: string = duplicate ? `${urlMain}/edit/${id}/duplicate` : id ? `${urlMain}/edit/${id}` : urlMain + '/add';
        this.router.navigateByUrl(url).then().catch();
    }

    /**
     * Redirect to add or edit group page
     * @param id
     */
    public addEditGroup(id?: string): void {
        const urlMain: string = `/lb/${this.applianceId}/maintenance/users/group`;
        const url: string = id ? `${urlMain}/edit/${id}` : urlMain + '/add';
        this.router.navigateByUrl(url).then().catch();
    }

    /**
     * Delete user association from organisation.
     * @param user
     */
    public deleteUser(user: any): void {
        this.enduranceRBACUsersApiService.deleteUser(user.id)
            .pipe(takeUntil(this.destroyed$))
            .subscribe({
                next: (): void => {
                    this.setUpPageData();
                },
                error: (error: ErrorResponseModel): void => {
                    this.logService.error('UsersListComponent: deleteUser', error.message);
                }
            });
    }

    /**
     * Delete role.
     * @param role
     */
    public deleteRole(role: any): void {
        this.enduranceRBACRolesApiService.deleteRole(role.id)
            .pipe(takeUntil(this.destroyed$))
            .subscribe({
                next: (): void => {
                    this.setUpPageData();
                },
                error: (error: ErrorResponseModel): void => {
                    this.logService.error('UsersListComponent: deleteUser', error.message);
                }
            });
    }

    /**
     * Delete role.
     * @param group
     */
    public deleteGroup(group: any): void {
        this.enduranceRBACGroupsApiService.deleteGroup(group.id)
            .pipe(takeUntil(this.destroyed$))
            .subscribe({
                next: (): void => {
                    this.setUpPageData();
                },
                error: (error: ErrorResponseModel): void => {
                    this.logService.error('UsersListComponent: deleteUser', error.message);
                }
            });
    }

    /**
     * Set up page data.
     * @private
     */
    private setUpPageData(): void {
        const apiCalls: any = {
            users: this.enduranceRBACUsersApiService.getAllUsers(),
            roles: this.enduranceRBACRolesApiService.getAllRoles(),
            groups: this.enduranceRBACGroupsApiService.getAllGroups(),
        };

        forkJoin(apiCalls)
            .pipe(takeUntil(this.destroyed$))
            .subscribe({
                next: (dataGroups: any): void => {
                    dataGroups['users'] = this.formatUsers(dataGroups);
                    this.data = dataGroups;
                },
                error: (error: ErrorResponseModel): void => {
                    this.logService.error('EnduranceUserListComponent: setUpPageData', error.message);
                }
            });
    }

    /**
     * Format users list
     * @param dataGroups
     * @private
     */
    private formatUsers(dataGroups: any): any[] {
        return dataGroups['users'].map((user: any) => ({
            ...user,
            name: user.firstName ? (user.firstName + ' ' + user.lastName) : user.name
        }));
    }
}
